import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { Marca } from '../componentes/Marca'
import './home.css'

/**
 * O site público.
 *
 * ─── DE ONDE VEM ESTA TELA ─────────────────────────────────────────
 *
 * Do mockup "Clinuma — o site, em três verdes", variante TURQUESA,
 * aprovada em 10/ago/2026. A ordem das faixas, as medidas, os pesos e
 * o desenho de cada peça são os dele:
 *
 *   nav → herói claro com a agenda ao lado → faixa de números →
 *   recursos em cartões com chip de ícone → [seções novas] →
 *   planos → a faixa escura de fechamento
 *
 * O único bloco escuro (#04353A) é o ÚLTIMO. Duas versões anteriores
 * desta página erraram por não abrir o mockup antes de desenhar.
 *
 * ─── O QUE FOI ACRESCENTADO ────────────────────────────────────────
 *
 * O mockup desenhou a home até "recursos". Entre ela e os planos
 * entram cinco seções na MESMA linguagem, porque o produto cresceu
 * muito desde 10/ago e a página não contava nada disso: prontuário
 * com carimbo de conselho, caixa e repasse, régua de WhatsApp, lista
 * de espera, trilha verificável.
 *
 * ─── AS DUAS REGRAS QUE NÃO MUDAM ──────────────────────────────────
 *
 * 1. Nada é "em breve" disfarçado de recurso. O que não existe está
 *    na seção final, com nome e motivo.
 * 2. Os planos vêm da API. Preço em dois lugares é preço que um dia
 *    diverge, e quem descobre é o cliente.
 */

type Plano = {
  codigo: string; nome: string; descricao: string | null
  preco_centavos: string; dias_teste: number
  max_profissionais: number | null; max_recursos: number | null
  max_usuarios: number | null; max_pacientes: number | null
  max_unidades: number | null; whatsapp: boolean; suporte: string
}

const reais = (c: string) => (Number(c) / 100).toLocaleString('pt-BR')

const SUPORTE: Record<string, string> = {
  EMAIL: 'Suporte por e-mail',
  CHAT: 'Suporte por chat',
  PRIORITARIO: 'Suporte prioritário',
}

const SECOES = [
  { id: 'recursos',  rotulo: 'Recursos' },
  { id: 'area',      rotulo: 'Para sua área' },
  { id: 'diferenca', rotulo: 'A diferença' },
  { id: 'planos',    rotulo: 'Planos' },
] as const

export function Home({ aoEntrar, aoCriarConta }: {
  aoEntrar(): void
  aoCriarConta(plano?: string): void
}) {
  const [planos, setPlanos] = useState<Plano[]>([])
  const [rolou, setRolou] = useState(false)
  const [ativa, setAtiva] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<{ planos: Plano[] }>('/v1/planos')
        setPlanos(r.planos)
      } catch { /* a página funciona sem a tabela de preços */ }
    })()
  }, [])

  /**
   * A barra sabe onde você está.
   *
   * Não é enfeite: numa página longa, um menu que nunca reage deixa a
   * pessoa sem noção de posição — e a sensação é de folheto, não de
   * produto. O sublinhado na seção corrente é o sinal mais barato de
   * que a página está viva.
   *
   * `IntersectionObserver` e não `scroll`: o navegador faz a conta, e
   * não há handler rodando a cada pixel rolado.
   */
  useEffect(() => {
    const aoRolar = () => setRolou(window.scrollY > 8)
    aoRolar()
    window.addEventListener('scroll', aoRolar, { passive: true })

    /*
     * A seção corrente é a ÚLTIMA âncora cujo topo já passou pela barra
     * — não a que está visível neste instante.
     *
     * A primeira versão usava interseção pura e ficava sem nenhum link
     * marcado na maior parte da rolagem: as quatro âncoras do menu têm
     * cinco seções entre elas ("A agenda", "O prontuário", "O dinheiro"…),
     * e enquanto o leitor estava numa dessas, nenhuma âncora intersectava.
     * Um menu que só acende de vez em quando é pior que um que nunca
     * acende: parece defeito.
     */
    const marcar = () => {
      const y = window.scrollY + 90
      let corrente: string | null = null
      for (const sec of SECOES) {
        const el = document.getElementById(sec.id)
        if (el && el.offsetTop <= y) corrente = sec.id
      }
      // Antes da primeira âncora, nenhuma acesa — o leitor está no herói.
      setAtiva(corrente)
    }
    marcar()
    window.addEventListener('scroll', marcar, { passive: true })
    return () => {
      window.removeEventListener('scroll', aoRolar)
      window.removeEventListener('scroll', marcar)
    }
  }, [])

  return (
    <div className="home">
      {/* ══ nav ═══════════════════════════════════════════════════ */}
      <nav className={`hm-nav ${rolou ? 'is-rolado' : ''}`}>
        <div className="hm-nav-dentro">
          <a className="hm-marca" href="#topo" aria-label="Clinuma, início">
            <Marca tamanho={26} />
          </a>
          <span className="hm-links">
            {SECOES.map((s) => (
              <a key={s.id} href={`#${s.id}`}
                 className={ativa === s.id ? 'is-ativa' : ''}
                 aria-current={ativa === s.id ? 'true' : undefined}>
                {s.rotulo}
              </a>
            ))}
          </span>
          <span className="hm-nav-acoes">
            <button className="botao botao--fantasma" onClick={aoEntrar}>Entrar</button>
            <button className="botao" onClick={() => aoCriarConta()}>Testar 14 dias</button>
          </span>
        </div>
      </nav>

      {/* ══ herói ═════════════════════════════════════════════════ */}
      <section className="hm-hero" id="topo">
        <div>
          <span className="hm-selo">Fisioterapia · Odontologia · Estética · Medicina</span>
          <h1>A agenda que não deixa marcar duas na mesma sala</h1>
          <p>
            Não é uma promessa de interface: quem recusa o choque é o banco de dados.
            Duas recepcionistas clicando ao mesmo tempo, e só uma marcação entra.
          </p>
          <div className="hm-acoes">
            <button className="botao botao--g" onClick={() => aoCriarConta()}>
              Começar o teste
            </button>
            <a className="botao botao--g botao--fantasma" href="#planos">Ver planos</a>
          </div>
          <p className="hm-obs">
            14 dias. Sem cartão. Seus dados saem em um clique se você desistir.
          </p>
        </div>

        <div className="hm-agenda" aria-label="Prévia da agenda">
          <div className="hm-agenda-topo">
            <b>Quinta, 14 de agosto</b><span>3 salas</span>
          </div>
          <div className="hm-colunas">
            <div style={{ borderBottom: '1px solid var(--cl-linha)' }} />
            <div className="hm-cab">Sala 1</div>
            <div className="hm-cab">Sala 2</div>
            <div className="hm-cab">Hidro</div>

            <div className="hm-hora">09:00</div>
            <Cel cor="var(--cl-ok)" nome="Marina R." estado="Confirmado" />
            <Cel cor="var(--cl-tinta-300)" nome="Paulo H." estado="Agendado" neutra />
            <div className="hm-cel" />

            <div className="hm-hora">09:50</div>
            <Cel cor="var(--cl-tq-600)" nome="Rogério A." estado="Em atendimento" />
            <div className="hm-cel" />
            <Cel cor="var(--cl-ok)" nome="Grupo · 3" estado="Confirmado" />

            <div className="hm-hora">10:40</div>
            <Cel cor="var(--cl-atencao)" nome="Encaixe" estado="Autorizado" />
            <Cel cor="var(--cl-erro)" nome="Tiago F." estado="Faltou" />
            <div className="hm-cel" />

            <div className="hm-hora">11:30</div>
            <div className="hm-cel" />
            <Cel cor="var(--cl-tinta-300)" nome="Beatriz L." estado="Agendado" neutra />
            <Cel cor="var(--cl-tinta-200)" nome="Limpeza" estado="Bloqueio" neutra />
          </div>
        </div>
      </section>

      {/* ══ faixa de números ══════════════════════════════════════ */}
      <div className="hm-faixa">
        <div>
          <div className="n">0</div>
          <div className="t">choques de sala possíveis — a restrição é do banco</div>
        </div>
        <div>
          <div className="n">1&nbsp;clique</div>
          <div className="t">para exportar tudo o que é seu</div>
        </div>
        <div>
          <div className="n">14&nbsp;dias</div>
          <div className="t">de teste, sem cartão</div>
        </div>
        <div>
          <div className="n">
            {planos.length > 0 ? `R$ ${reais(planos[0]!.preco_centavos)}` : 'Tudo'}
          </div>
          <div className="t">
            {planos.length > 0 ? 'por mês, tudo incluído' : 'incluído em qualquer plano'}
          </div>
        </div>
      </div>

      {/* ══ recursos ══════════════════════════════════════════════ */}
      <section className="hm-sec" id="recursos">
        <div className="hm-dentro">
          <h2>O sistema inteiro, em todos os planos</h2>
          <p className="hm-sub">
            Prontuário, backup e exportação não são upsell. Vender isso separado é o que
            faz software de saúde ser odiado.
          </p>
          <div className="hm-cards">
            <Card icone={<IconeAgenda />}
                  titulo="Agenda por sala e por profissional">
              A mesma semana vista pelos dois lados. O tempo de limpeza entra sozinho, e
              encaixe exige autorização.
            </Card>
            <Card icone={<IconeFicha />}
                  titulo="Prontuário que a resolução aceita">
              Avaliação e evolução separadas. Ficha assinada não se altera: correção vira
              adendo, e o original fica.
            </Card>
            <Card icone={<IconeConversa />}
                  titulo="WhatsApp no número da sua clínica">
              O número é seu, no seu Business Portfolio. Você tira nosso acesso quando
              quiser, sem pedir.
            </Card>
            <Card icone={<IconeEscudo />}
                  titulo="Uma clínica nunca vê a outra">
              O isolamento é do banco, não do código. Testado com 40 conexões alternando
              clínicas ao mesmo tempo.
            </Card>
          </div>
        </div>
      </section>

      {/* ══ a semana ══════════════════════════════════════════════ */}
      <section className="hm-sec hm-sec--tinta">
        <div className="hm-dentro">
          <p className="hm-rot">A agenda</p>
          <h2>A semana como ela é, não como o sistema prefere</h2>
          <p className="hm-sub">
            Sala, equipamento e profissional são recursos independentes. A hidro com
            capacidade 3 aceita a aula em grupo; a sala de avaliação, não.
          </p>
          <ul className="hm-lista">
            <li>
              <span><strong>O almoço não precisa de bloqueio falso.</strong> Quantas faixas
              de trabalho quiser por dia — segunda pode ser 08:00–12:00 e 14:00–19:00, e o
              intervalo simplesmente não existe para o sistema.</span>
            </li>
            <li>
              <span><strong>Três números que os outros juntam num só.</strong> De quantos em
              quantos minutos a grade desenha, de quantos em quantos os horários são
              oferecidos, e qual o menor atendimento possível. Separados, a sessão de 50
              minutos pode começar 9:00, 9:15 ou 9:30.</span>
            </li>
            <li>
              <span><strong>Quem desmarcou não é quem faltou.</strong> Estados diferentes,
              cores diferentes, consequências diferentes: quem avisou tem a sessão devolvida
              ao pacote.</span>
            </li>
            <li>
              <span><strong>Mover por teclado e por toque.</strong> Arrastar com o mouse não
              é o único caminho — é o critério 2.5.7 da WCAG, que quase toda agenda do
              mercado reprova.</span>
            </li>
            <li>
              <span><strong>Lista de espera que vira atendimento.</strong> Quando alguém
              desmarca, o sistema mostra quem cabe naquele horário — filtrado por dia da
              semana, faixa preferida e quem já foi chamado hoje. Não a fila inteira para
              alguém garimpar.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* ══ prontuário ════════════════════════════════════════════ */}
      <section className="hm-sec">
        <div className="hm-dentro">
          <p className="hm-rot">O prontuário</p>
          <h2>Documento, não caixa de texto</h2>
          <p className="hm-sub">
            Avaliação, evolução, reavaliação e alta são formulários diferentes. O sistema
            traz modelos prontos por especialidade — você importa o que serve e edita, porque
            depois de importado o formulário é seu.
          </p>
          <div className="hm-cards">
            <Card icone={<IconeCadeado />} titulo="Assinada não se altera">
              Correção entra como adendo, com o original visível ao lado. Riscar o que
              estava escrito antes é o que invalida um prontuário.
            </Card>
            <Card icone={<IconeCarimbo />} titulo="O carimbo congela na hora">
              Nome, conselho, número e UF são copiados para dentro da ficha no instante da
              assinatura. Se o CREFITO mudar amanhã, a ficha de hoje não muda junto.
            </Card>
            <Card icone={<IconeRegua />} titulo="22 escalas com faixa">
              Berg, Barthel, MIF, Tinetti, TUG, DASH, WOMAC, Oswestry, Borg. Berg vai até 56
              — um 65 digitado sem trava viraria “melhora” no gráfico.
            </Card>
            <Card icone={<IconeGrafico />} titulo="A evolução desenha sozinha">
              A dor medida sessão a sessão vira linha. O paciente vê que está melhorando, e
              você tem o registro que sustenta a alta.
            </Card>
          </div>
        </div>
      </section>

      {/* ══ dinheiro ══════════════════════════════════════════════ */}
      <section className="hm-sec hm-sec--tinta">
        <div className="hm-dentro">
          <p className="hm-rot">O dinheiro</p>
          <h2>Três datas que quase todo sistema junta em uma</h2>
          <p className="hm-sub">
            Competência é quando o serviço foi prestado. Pago é quando o paciente quitou.
            Liquidado é quando o dinheiro caiu na conta. Cartão em D+30 vive semanas em
            “pago” sem um centavo em caixa.
          </p>
          <div className="hm-cards">
            <Card icone={<IconeCaixa />} titulo="Fechamento de caixa">
              Você digita só o que contou na gaveta; o esperado o sistema calcula. Caixa com
              diferença fecha como divergente, exige justificativa e não reabre depois.
            </Card>
            <Card icone={<IconeRepasse />} titulo="Repasse ao profissional">
              Só entra no repasse o que o paciente já pagou. Repassar comissão de dinheiro
              que não caiu é a clínica financiando o profissional com o capital de giro dela.
            </Card>
            <Card icone={<IconePacote />} titulo="Pacote com saldo real">
              Dez sessões vendidas, três usadas, e o saldo é do banco. Quem desmarcou com
              aviso devolve a sessão; quem faltou, não.
            </Card>
          </div>
        </div>
      </section>

      {/* ══ conversa ══════════════════════════════════════════════ */}
      <section className="hm-sec">
        <div className="hm-dentro">
          <p className="hm-rot">A conversa com o paciente</p>
          <h2>A régua é da clínica, não de quem marcou</h2>
          <p className="hm-sub">
            Confirmação, lembrete e pós-atendimento com antecedência em horas — porque
            “3 horas antes” derruba mais falta do que a véspera.
          </p>
          <ul className="hm-lista">
            <li>
              <span><strong>Quem faz série de sessões não recebe trinta lembretes.</strong>{' '}
              Paciente de reabilitação vem duas ou três vezes por semana; a régua tem silêncio
              mínimo entre mensagens e opção de avisar só na primeira vez.</span>
            </li>
            <li>
              <span><strong>Faixa de silêncio.</strong> Nada sai fora do horário que a clínica
              definir. Mensagem às 6h não é lembrete, é incômodo — e incômodo vira bloqueio,
              que derruba a entrega para todos os pacientes.</span>
            </li>
            <li>
              <span><strong>Quem pediu para parar, para.</strong> Lembrete é comunicação
              transacional e sai por padrão, mas uma revogação cala. Retorno é oferta e exige
              autorização explícita.</span>
            </li>
          </ul>
          <p className="hm-sub" style={{ marginTop: 24, marginBottom: 0 }}>
            <strong>Isto ainda não envia.</strong> A régua, o canal e o controle de custo estão
            construídos e testados; o envio depende da liberação do nosso aplicativo pela Meta,
            que não é uma data que a gente controle. Está escrito aqui porque preferimos você
            saber agora do que depois de assinar.
          </p>
        </div>
      </section>

      {/* ══ segurança ═════════════════════════════════════════════ */}
      <section className="hm-sec hm-sec--tinta">
        <div className="hm-dentro">
          <p className="hm-rot">Segurança e LGPD</p>
          <h2>Isolamento não é feature, é o produto</h2>
          <p className="hm-sub">
            É software de saúde. O isolamento entre clínicas é do banco, linha por linha —
            não uma condição no código que alguém pode esquecer numa consulta nova.
          </p>
          <div className="hm-cards">
            <Card icone={<IconeCorrente />} titulo="Trilha que se verifica">
              Cada evento é encadeado por hash ao anterior. Alterar ou arrancar uma linha
              quebra a corrente de forma detectável — e há um botão que confere e diz qual.
            </Card>
            <Card icone={<IconeOlho />} titulo="Quem olhou o prontuário">
              Direito do paciente pela LGPD. Não agrega por dia: “a mesma pessoa abriu 14
              vezes hoje” é exatamente o padrão que interessa.
            </Card>
            <Card icone={<IconeChave />} titulo="A recepção não vê prontuário">
              É o padrão de fábrica, e a aba nem aparece. Dar acesso é decisão explícita do
              dono, registrada com quem concedeu e quando.
            </Card>
            <Card icone={<IconeBackup />} titulo="Restauração ensaiada">
              Aos domingos o sistema restaura o backup num banco separado e confere que ele
              volta. Backup que nunca foi restaurado é esperança, não backup.
            </Card>
          </div>
        </div>
      </section>

      {/* ══ a diferença ═══════════════════════════════════════════ */}
      <section className="hm-sec" id="diferenca">
        <div className="hm-dentro">
          <p className="hm-rot">Lado a lado</p>
          <h2>Quatro diferenças que aparecem na primeira semana</h2>
          <p className="hm-sub">
            Não são opiniões sobre design. São comportamentos observados nos sistemas que as
            clínicas usam hoje, e a decisão que tomamos em cada um.
          </p>

          <div className="hm-versus">
            <div className="hm-versus-l hm-versus-cab">
              <div>O caso</div><div>Como costuma ser</div><div>Aqui</div>
            </div>
            <Versus caso="A clínica fecha para o almoço"
                    deles="Uma faixa por dia — é preciso criar um bloqueio falso todo dia"
                    nossa="Quantas faixas quiser; o intervalo não existe para o sistema" />
            <Versus caso="Sessão de 50 minutos"
                    deles="Um número resolve desenho, oferta e duração — arredonda para 45 ou 60"
                    nossa="Três números separados; 50 min começando de 15 em 15" />
            <Versus caso="Paciente avisou que não vem"
                    deles="Mesmo estado de quem faltou, e a sessão do pacote se perde igual"
                    nossa="Desmarcou ≠ faltou; quem avisou tem a sessão devolvida" />
            <Versus caso="Configurar o sistema"
                    deles="Vinte e cinco itens em ordem alfabética, sem busca e sem agrupamento"
                    nossa="Seis grupos com nome de pergunta, agrupados por intenção" />
          </div>
        </div>
      </section>

      {/* ══ para sua área ═════════════════════════════════════════ */}
      <section className="hm-sec hm-sec--tinta" id="area">
        <div className="hm-dentro">
          <p className="hm-rot">Para sua área</p>
          <h2>Multi-especialidade de verdade</h2>
          <p className="hm-sub">
            A clínica começa vazia, de propósito. Uma clínica de estética que abre o sistema
            e encontra “Endodontia” cadastrada entendeu tudo sobre para quem aquele software
            foi feito.
          </p>
          <div className="hm-cards">
            <Card icone={<IconeGrafico />} titulo="Fisioterapia e reabilitação">
              Pacote de sessões com saldo, escalas de desfecho, reavaliação a cada dez, alta
              com motivo. É a vertical-piloto e a mais completa hoje.
            </Card>
            <Card icone={<IconeFicha />} titulo="Odontologia">
              Cadeira como recurso com tempo de preparo, procedimento por dente, e a agenda
              por cadeira em vez de por profissional quando faz mais sentido.
            </Card>
            <Card icone={<IconeRegua />} titulo="Estética">
              Fototipo na avaliação, contraindicação registrada, perimetria acompanhada, e
              profissional sem conselho — porque esteticista não tem, e o sistema aceita.
            </Card>
            <Card icone={<IconeCadeado />} titulo="Consultório médico">
              Prontuário com anamnese, sinais vitais, e a mesma trilha de acesso: quem abriu
              o registro de quem, e quando.
            </Card>
          </div>
        </div>
      </section>

      {/* ══ planos ════════════════════════════════════════════════ */}
      <section className="hm-sec" id="planos">
        <div className="hm-dentro">
          <h2>Preço honesto, sem degrau escondido</h2>
          <p className="hm-sub">
            O preço trava na contratação. Se a tabela subir depois, a sua conta não sobe junto.
          </p>

          <div className="hm-planos">
            {planos.length === 0 && <p className="ajuda">Carregando os planos…</p>}
            {planos.map((p, i) => (
              <article key={p.codigo} className={`hm-pl ${i === 1 ? 'dest' : ''}`}>
                {/* Não é "mais escolhido": seria prova social inventada,
                    e ainda não temos cliente para contar. */}
                <div className="marca-pl">{i === 1 ? 'Com mais de uma sala' : ' '}</div>
                <h3>{p.nome}</h3>
                <p className="d">{p.descricao}</p>
                <div className="p">
                  <span className="c">R$</span>
                  <span className="v">{reais(p.preco_centavos)}</span>
                  <span className="m">/mês</span>
                </div>
                <ul>
                  <li>
                    {p.max_profissionais ? `${p.max_profissionais} profissionais` : 'Profissionais sem limite'}
                    {p.max_recursos ? `, ${p.max_recursos} salas` : ''}
                  </li>
                  <li>
                    {p.max_pacientes
                      ? `${p.max_pacientes.toLocaleString('pt-BR')} pacientes`
                      : 'Pacientes sem limite'}
                  </li>
                  <li>Agenda, prontuário e financeiro</li>
                  {p.whatsapp && <li>WhatsApp no número da clínica</li>}
                  <li>{SUPORTE[p.suporte] ?? p.suporte}</li>
                </ul>
                <button className={`botao ${i === 1 ? '' : 'botao--fantasma'}`}
                        onClick={() => aoCriarConta(p.codigo)}>
                  Testar {p.dias_teste} dias
                </button>
              </article>
            ))}
          </div>

          <p className="hm-rodape-planos">
            <strong>Em todos:</strong> prontuário completo, backup diário com restauração
            testada, e exportação de tudo o que é seu — a qualquer momento, sem pedir para
            ninguém.
          </p>
        </div>
      </section>

      {/* ══ o que ainda não existe ════════════════════════════════ */}
      <section className="hm-sec hm-sec--tinta">
        <div className="hm-dentro">
          <p className="hm-rot">Honestidade</p>
          <h2>O que ainda não existe</h2>
          <p className="hm-sub">
            Dizer isto agora é mais barato do que você descobrir sozinho depois de migrar a
            clínica inteira.
          </p>
          <div className="hm-obra">
            <div className="hm-obra-item">
              <b>Envio automático no WhatsApp</b>
              <span>
                A régua, o canal e o custo estão prontos. Falta a liberação do aplicativo
                pela Meta — não é uma data que controlamos.
              </span>
            </div>
            <div className="hm-obra-item">
              <b>Cobrança por Pix ou cartão</b>
              <span>
                Sem gateway na primeira versão, por decisão. A assinatura é combinada
                direto, e o teto declarado é de cerca de 20 clínicas.
              </span>
            </div>
            <div className="hm-obra-item">
              <b>Importar do sistema atual</b>
              <span>
                Ainda manual. Se você vem de outro software, a migração é feita com a gente
                junto e sem custo enquanto o importador não existir.
              </span>
            </div>
            <div className="hm-obra-item">
              <b>Verificação em duas etapas</b>
              <span>
                Está na fila. Hoje a proteção é senha com argon2, sessão que se revoga na
                hora e trilha de acesso.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ══ a prova — o único bloco escuro, e ele fecha ═══════════ */}
      <section className="hm-prova">
        <div className="hm-prova-dentro">
          <p className="rot">Por que existimos</p>
          <blockquote>
            “Marcaram dois pacientes na mesma sala de novo. O sistema deixou.”
          </blockquote>
          <cite>
            É a frase que aparece em toda avaliação ruim de todo concorrente — não é um
            cliente nosso falando. Foi o primeiro problema que resolvemos, e resolvemos no
            banco, onde nenhum código consegue esquecer.
          </cite>
        </div>
      </section>

      <footer className="hm-pe">
        <div className="hm-pe-dentro">
          <Marca tamanho={22} />
          <p>
            Clinuma · software de gestão para clínicas de qualquer especialidade ·{' '}
            <a href="mailto:suporte@clinuma.com.br">suporte@clinuma.com.br</a>
          </p>
          <p>
            Feito no Brasil, com os dados no Brasil. Isolamento por clínica, trilha de acesso
            e exportação completa fazem parte do produto — não de um plano superior.
          </p>
        </div>
      </footer>
    </div>
  )
}

/* ══ peças ═════════════════════════════════════════════════════════ */

/** Célula da agenda do herói. Acromática: a cor mora no filete. */
function Cel({ cor, nome, estado, neutra = false }: {
  cor: string; nome: string; estado: string; neutra?: boolean
}) {
  return (
    <div className="hm-cel">
      <div className={`hm-ficha ${neutra ? 'neutra' : ''}`}
           style={{ ['--fc' as string]: cor }}>
        <b>{nome}</b><i>{estado}</i>
      </div>
    </div>
  )
}

function Card({ icone, titulo, children }: {
  icone: ReactNode; titulo: string; children: ReactNode
}) {
  return (
    <div className="hm-card">
      <div className="ico">{icone}</div>
      <h3>{titulo}</h3>
      <p>{children}</p>
    </div>
  )
}

function Versus({ caso, deles, nossa }: { caso: string; deles: string; nossa: string }) {
  return (
    <div className="hm-versus-l">
      <div className="caso">{caso}</div>
      <div className="deles">{deles}</div>
      <div className="nossa">{nossa}</div>
    </div>
  )
}

/* ── ícones ────────────────────────────────────────────────────────
 * Traçado de 2px em `currentColor`, 24×24, como no mockup. Herdam a
 * cor do chip (--cl-tq-600) e acompanham o tema sem segunda paleta.
 * ────────────────────────────────────────────────────────────────── */

const svg = {
  width: 19, height: 19, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const IconeAgenda = () => (
  <svg {...svg}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" /></svg>
)
const IconeFicha = () => (
  <svg {...svg}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15h6M9 11h3" /></svg>
)
const IconeConversa = () => (
  <svg {...svg}><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-4-1L3 21l2.1-5a8.4 8.4 0 0 1-1-4 8.4 8.4 0 0 1 8.4-8.4 8.4 8.4 0 0 1 8.5 8.4z" /></svg>
)
const IconeEscudo = () => (
  <svg {...svg}><path d="M12 2 4 6v6c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" /></svg>
)
const IconeCadeado = () => (
  <svg {...svg}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
)
const IconeCarimbo = () => (
  <svg {...svg}><path d="M6 20h12M9 16v-2.5a4 4 0 0 0-1-2.6A4 4 0 0 1 12 4a4 4 0 0 1 4 6.9 4 4 0 0 0-1 2.6V16" /><rect x="6" y="16" width="12" height="3" rx="1" /></svg>
)
const IconeRegua = () => (
  <svg {...svg}><rect x="2" y="8" width="20" height="8" rx="2" /><path d="M7 8v3M12 8v4M17 8v3" /></svg>
)
const IconeGrafico = () => (
  <svg {...svg}><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></svg>
)
const IconeCaixa = () => (
  <svg {...svg}><rect x="2" y="6" width="20" height="13" rx="2" /><path d="M2 11h20" /><circle cx="17" cy="15" r="1.2" /></svg>
)
const IconeRepasse = () => (
  <svg {...svg}><path d="M4 8h13l-3-3M20 16H7l3 3" /></svg>
)
const IconePacote = () => (
  <svg {...svg}><path d="M21 8v9a2 2 0 0 1-1 1.7l-7 3.9a2 2 0 0 1-2 0l-7-3.9A2 2 0 0 1 3 17V8" /><path d="m3.3 7 8.7 4.8L20.7 7 12 2.2z" /><path d="M12 11.8V22" /></svg>
)
const IconeCorrente = () => (
  <svg {...svg}><path d="M9.5 14.5a3.5 3.5 0 0 1 0-5l2-2a3.5 3.5 0 0 1 5 5l-.8.8" /><path d="M14.5 9.5a3.5 3.5 0 0 1 0 5l-2 2a3.5 3.5 0 0 1-5-5l.8-.8" /></svg>
)
const IconeOlho = () => (
  <svg {...svg}><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" /><circle cx="12" cy="12" r="2.6" /></svg>
)
const IconeChave = () => (
  <svg {...svg}><circle cx="8" cy="12" r="3.5" /><path d="M11.5 12H21M18 12v3M15 12v2.5" /></svg>
)
const IconeBackup = () => (
  <svg {...svg}><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" /><path d="M4 12v6c0 1.7 3.6 3 8 3 1.4 0 2.7-.13 3.8-.36" /><path d="m17 17 2 2 3.5-3.5" /></svg>
)
